import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { Staff, StaffFormData, PersonalInfo, HealthInfo, EmploymentInfo } from '../../types';
import { getCurrentUser } from '../Login/queries';

// ==================== TYPE DEFINITIONS ====================

interface StaffLocationResponse {
  id: number;
  longitude: string;
  latitude: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
}

interface UserAPIResponse {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  userName: string;
  roleId?: number;
  siteId?: number | null;
  managerId?: number | null;
  workShiftId?: number | null;
  workShift?: {
    id: number;
    name: string;
    startTime: string;
    endTime: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  profilePhoto?: string | null;
  faceImageUrl?: string | null;
  roles?: {
    id: number;
    name: string;
    description: string;
  };
  site?: {
    id: number;
    siteId: string;
    name: string;
  } | null;
  manager?: {
    id: number;
    name: string;
  } | null;
  staff_location?: StaffLocationResponse | null;

  // Personal Info - Flat structure from API
  dateOfBirth?: string | null;
  gender?: string | null;
  bloodGroup?: string | null;
  maritalStatus?: string | null;
  aadharNumber?: string | null;
  panNumber?: string | null;
  address?: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  } | null;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  } | null;

  // Employment Info - Flat structure from API
  designation?: string | null;

  // Health Info - Flat structure from API
  height?: string | null;
  weight?: string | null;
  bloodPressure?: string | null;
  medicalConditions?: string | null;
  allergies?: string | null;
  currentMedications?: string | null;
  vaccinationStatus?: string | null;
  lastHealthCheckup?: string | null;

  // Documents array
  documents?: {
    type: string;
    name: string;
    url: string;
    s3Key?: string;
    mimeType?: string;
    size?: number;
    uploadedAt?: string;
  }[];
}

interface UsersListAPIResponse {
  users: UserAPIResponse[];
}

interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  userName: string;
  phoneNumber: string;
  roleId: number;
  managerId?: number | null;
  siteId?: number | null;
  workShiftId?: number | null;

  // NESTED structure to match API
  personalInfo: {
    dateOfBirth: string;
    gender: string;
    bloodGroup: string;
    maritalStatus: string;
    aadharNumber: string;
    panNumber: string;
    address: {
      street: string;
      city: string;
      state: string;
      pincode: string;
    };
    emergencyContact: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  healthInfo: {
    height: string;
    weight: string;
    bloodPressure: string;
    medicalConditions: string;
    allergies: string;
    currentMedications: string;
    vaccinationStatus: string;
    lastHealthCheckup: string;
  };
  employmentInfo: {
    dateOfJoining: string;
    employeeId: string;
    department: string;
    designation: string;
    salary: string;
  };
}

interface UpdateUserRequest {
  id: number;
  name?: string;
  email?: string;
  userName?: string;
  phoneNumber?: string;
  managerId?: number | null;
  siteId?: number | null;
  workShiftId?: number | null;

  // NESTED structure to match API
  personalInfo?: {
    dateOfBirth?: string;
    gender?: string;
    bloodGroup?: string;
    maritalStatus?: string;
    aadharNumber?: string;
    panNumber?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      pincode: string;
    };
    emergencyContact?: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  healthInfo?: {
    height?: string;
    weight?: string;
    bloodPressure?: string;
    medicalConditions?: string;
    allergies?: string;
    currentMedications?: string;
    vaccinationStatus?: string;
    lastHealthCheckup?: string;
  };
  employmentInfo?: {
    dateOfJoining?: string;
    employeeId?: string;
    department?: string;
    designation?: string;
    salary?: string;
  };
}

interface AssignRoleRequest {
  userId: number;
  roleId: number;
}

interface AssignSiteRequest {
  siteId: number;
  staffId: number;
  currentStaffIds: number[];
}

interface UpdateSiteRequest {
  id: number;
  siteId: string;
  name: string;
  description: string;
  longitude: number;
  latitude: number;
  threshold_distance: number;
  users: number[];
}

interface UserDocumentsResponse {
  success: boolean;
  user: {
    id: number;
    name: string;
  };
  profilePhoto: string | null;
  facePhoto: string | null;
  aadharCard: string | null;
  panCard: string | null;
  resume: string | null;
  medicalCertificate: string | null;
  otherDocuments: { url: string; name: string; size: number; type: string; s3Key: string }[];
}

// ==================== API CALLS ====================

// GET all users/staffs with locations
const fetchStaffs = async (): Promise<Staff[]> => {
  // First, get all users
  const usersResponse = await apiClient.get<UsersListAPIResponse | UserAPIResponse[]>('/api/user/');
  const rawUsers: UserAPIResponse[] = Array.isArray(usersResponse.data)
    ? usersResponse.data
    : (usersResponse.data as UsersListAPIResponse).users;

  const staffsWithLocations = rawUsers.map((user) => {
    const certDoc = user.documents?.find((d: any) => d.type === 'certificate');
    const staff: Staff = {
      id: user.id.toString(),
      name: user.name,
      userName: user.userName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      role: user.roles?.name || 'N/A',
      reportingTo: user.manager?.name || 'N/A',
      site: user.site?.name || 'N/A',
      roleId: user.roles?.id,
      siteId: user.site?.id || null,
      managerId: user.manager?.id || null,
      workShiftId: user.workShift?.id || user.workShiftId || null,
      designation: user.designation || '',
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      profilePhoto: user.profilePhoto || null,
      faceImageUrl: user.faceImageUrl || null,
      medicalCertificateUrl: certDoc?.url || null,
      staff_location: user.staff_location || null,
      coordinates: user.staff_location ? {
        latitude: parseFloat(user.staff_location.latitude),
        longitude: parseFloat(user.staff_location.longitude),
      } : undefined,
      personalInfo: {
        dateOfBirth: user.dateOfBirth || '',
        gender: (user.gender as any) || '',
        bloodGroup: (user.bloodGroup as any) || '',
        maritalStatus: (user.maritalStatus as any) || '',
        aadharNumber: user.aadharNumber || '',
        panNumber: user.panNumber || '',
        faceImage: null,
        address: user.address || { street: '', city: '', state: '', pincode: '' },
        emergencyContact: user.emergencyContact || { name: '', phone: '', relationship: '' },
      },
      healthInfo: {
        height: user.height || '',
        weight: user.weight || '',
        bloodPressure: user.bloodPressure || '',
        medicalConditions: user.medicalConditions || '',
        allergies: user.allergies || '',
        currentMedications: user.currentMedications || '',
        vaccinationStatus: user.vaccinationStatus || '',
        lastHealthCheckup: user.lastHealthCheckup || '',
        medicalCertificate: null,
      },
    };
    return staff;
  });

  return staffsWithLocations;
};

// GET user/staff by ID
const fetchStaffById = async (id: string): Promise<Staff> => {
  const raw = await apiClient.get<{ user: UserAPIResponse } | UserAPIResponse>(`/api/user/${id}`);
  const rawData = raw.data;
  const response = { data: { user: 'user' in rawData ? (rawData as { user: UserAPIResponse }).user : rawData as UserAPIResponse } };
  const user = response.data.user;
  const certDoc = user.documents?.find(d => d.type === 'certificate');

  return {
    id: user.id.toString(),
    name: user.name,
    userName: user.userName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    role: user.roles?.name || 'N/A',
    reportingTo: user.manager?.name || 'N/A',
    site: user.site?.name || 'N/A',
    roleId: user.roles?.id,
    siteId: user.site?.id || null,
    managerId: user.manager?.id || null,
    workShiftId: user.workShift?.id || user.workShiftId || null,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    profilePhoto: user.profilePhoto || null,
    faceImageUrl: user.faceImageUrl || null,
    designation: user.designation || '',
    medicalCertificateUrl: certDoc?.url || null,

    // Parse location data
    staff_location: user.staff_location || null,
    coordinates: user.staff_location ? {
      latitude: parseFloat(user.staff_location.latitude),
      longitude: parseFloat(user.staff_location.longitude),
    } : undefined,

    // Map flat API fields to nested structure
    personalInfo: {
      dateOfBirth: user.dateOfBirth || '',
      gender: (user.gender as any) || '',
      bloodGroup: (user.bloodGroup as any) || '',
      maritalStatus: (user.maritalStatus as any) || '',
      aadharNumber: user.aadharNumber || '',
      panNumber: user.panNumber || '',
      faceImage: null,
      address: user.address || {
        street: '',
        city: '',
        state: '',
        pincode: '',
      },
      emergencyContact: user.emergencyContact || {
        name: '',
        phone: '',
        relationship: '',
      },
    },
    healthInfo: {
      height: user.height || '',
      weight: user.weight || '',
      bloodPressure: user.bloodPressure || '',
      medicalConditions: user.medicalConditions || '',
      allergies: user.allergies || '',
      currentMedications: user.currentMedications || '',
      vaccinationStatus: user.vaccinationStatus || '',
      lastHealthCheckup: user.lastHealthCheckup || '',
      medicalCertificate: null,
    },
  };
};

// REGISTER FACE for attendance
const registerFace = async (userId: number, faceImage: File): Promise<void> => {
  const formData = new FormData();
  formData.append('userId', userId.toString());
  formData.append('image', faceImage);

  const response = await apiClient.post('/api/attendance/register-face', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// UPLOAD MEDICAL CERTIFICATE
const uploadMedicalCertificate = async (userId: number, certificate: File): Promise<void> => {
  const formData = new FormData();
  formData.append('userId', userId.toString());
  formData.append('document', certificate);
  formData.append('documentType', 'medicalCertificate');

  const response = await apiClient.post('/api/documents/upload-document', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// UPLOAD PROFILE PICTURE
const uploadProfilePicture = async (userId: number, photo: File): Promise<void> => {
  const formData = new FormData();
  formData.append('userId', userId.toString());
  formData.append('photo', photo);

  const response = await apiClient.post('/api/documents/upload-profile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// CREATE user/staff
const createStaff = async (data: StaffFormData): Promise<Staff> => {
  const faceImage = data.personalInfo.faceImage;
  const medicalCertificate = data.healthInfo.medicalCertificate;

  const personalInfoPayload = {
    dateOfBirth: data.personalInfo.dateOfBirth,
    gender: data.personalInfo.gender,
    bloodGroup: data.personalInfo.bloodGroup,
    maritalStatus: data.personalInfo.maritalStatus,
    aadharNumber: data.personalInfo.aadharNumber,
    panNumber: data.personalInfo.panNumber,
    address: data.personalInfo.address,
    emergencyContact: data.personalInfo.emergencyContact,
  };

  const healthInfoPayload = {
    height: data.healthInfo.height,
    weight: data.healthInfo.weight,
    bloodPressure: data.healthInfo.bloodPressure,
    medicalConditions: data.healthInfo.medicalConditions,
    allergies: data.healthInfo.allergies,
    currentMedications: data.healthInfo.currentMedications,
    vaccinationStatus: data.healthInfo.vaccinationStatus,
    lastHealthCheckup: data.healthInfo.lastHealthCheckup,
  };

  const employmentInfoPayload = {
    dateOfJoining: data.employmentInfo.dateOfJoining,
    employeeId: data.employmentInfo.employeeId,
    department: data.employmentInfo.department,
    designation: data.employmentInfo.designation,
    salary: data.employmentInfo.salary,
  };

  // Always use multipart/form-data — face image (attendance photo) is required
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('email', data.email);
  formData.append('password', data.password || '1234');
  formData.append('userName', data.userName);
  formData.append('phoneNumber', data.phoneNumber);
  formData.append('roleId', data.roleId.toString());
  if (data.managerId) formData.append('managerId', data.managerId.toString());
  if (data.siteId) formData.append('siteId', data.siteId.toString());
  if (data.workShiftId) formData.append('workShiftId', data.workShiftId.toString());
  formData.append('personalInfo', JSON.stringify(personalInfoPayload));
  formData.append('healthInfo', JSON.stringify(healthInfoPayload));
  formData.append('employmentInfo', JSON.stringify(employmentInfoPayload));

  // Build documentTypes and documents arrays
  const documentTypes: string[] = ['other']; // face image (attendance photo) — always present
  if (medicalCertificate) documentTypes.push('certificate');

  formData.append('documentTypes', JSON.stringify(documentTypes));
  formData.append('documents', faceImage!); // attendance photo first
  if (medicalCertificate) formData.append('documents', medicalCertificate);

  formData.append('organization_id', getCurrentUser()?.orgId.toString());

  const response = await apiClient.post<{ user: UserAPIResponse }>('/api/user/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const user = response.data.user;
  const certDoc = user.documents?.find(d => d.type === 'certificate');

  return {
    id: user.id.toString(),
    name: user.name,
    userName: user.userName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    role: user.roles?.name || 'N/A',
    reportingTo: user.manager?.name || 'N/A',
    site: user.site?.name || 'N/A',
    roleId: user.roleId,
    siteId: user.siteId,
    managerId: user.managerId,
    workShiftId: user.workShift?.id || user.workShiftId || null,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    staff_location: null,
    coordinates: undefined,
    medicalCertificateUrl: certDoc?.url || null,
    personalInfo: {
      dateOfBirth: user.dateOfBirth || '',
      gender: (user.gender as any) || '',
      bloodGroup: (user.bloodGroup as any) || '',
      maritalStatus: (user.maritalStatus as any) || '',
      aadharNumber: user.aadharNumber || '',
      panNumber: user.panNumber || '',
      faceImage: null,
      address: user.address || {
        street: '',
        city: '',
        state: '',
        pincode: '',
      },
      emergencyContact: user.emergencyContact || {
        name: '',
        phone: '',
        relationship: '',
      },
    },
    healthInfo: {
      height: user.height || '',
      weight: user.weight || '',
      bloodPressure: user.bloodPressure || '',
      medicalConditions: user.medicalConditions || '',
      allergies: user.allergies || '',
      currentMedications: user.currentMedications || '',
      vaccinationStatus: user.vaccinationStatus || '',
      lastHealthCheckup: user.lastHealthCheckup || '',
      medicalCertificate: null,
    },
  };
};

// UPDATE user/staff
const updateStaff = async (data: Staff): Promise<Staff> => {
  const payload: UpdateUserRequest = {
    id: parseInt(data.id.toString()),
    name: data.name,
    email: data.email,
    userName: data.userName,
    phoneNumber: data.phoneNumber,
    managerId: data.managerId || null,
    siteId: data.siteId || null,
    workShiftId: data.workShiftId || null,

    // NESTED structure - send as-is
    personalInfo: data.personalInfo ? {
      dateOfBirth: data.personalInfo.dateOfBirth,
      gender: data.personalInfo.gender,
      bloodGroup: data.personalInfo.bloodGroup,
      maritalStatus: data.personalInfo.maritalStatus,
      aadharNumber: data.personalInfo.aadharNumber,
      panNumber: data.personalInfo.panNumber,
      address: data.personalInfo.address,
      emergencyContact: data.personalInfo.emergencyContact,
    } : undefined,
    healthInfo: data.healthInfo ? {
      height: data.healthInfo.height,
      weight: data.healthInfo.weight,
      bloodPressure: data.healthInfo.bloodPressure,
      medicalConditions: data.healthInfo.medicalConditions,
      allergies: data.healthInfo.allergies,
      currentMedications: data.healthInfo.currentMedications,
      vaccinationStatus: data.healthInfo.vaccinationStatus,
      lastHealthCheckup: data.healthInfo.lastHealthCheckup,
    } : undefined,
    employmentInfo: data.employmentInfo ? {
      dateOfJoining: data.employmentInfo.dateOfJoining,
      employeeId: data.employmentInfo.employeeId,
      department: data.employmentInfo.department,
      designation: data.employmentInfo.designation,
      salary: data.employmentInfo.salary,
    } : undefined,
  };

  const response = await apiClient.put<{ updatedUser: UserAPIResponse } | UserAPIResponse>('/api/user', payload);
  const rawUpdate = response.data;
  const user: UserAPIResponse = 'updatedUser' in rawUpdate ? (rawUpdate as { updatedUser: UserAPIResponse }).updatedUser : rawUpdate as UserAPIResponse;

  return {
    id: user.id.toString(),
    name: user.name,
    userName: user.userName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    role: user.roles?.name || 'N/A',
    reportingTo: user.manager?.name || 'N/A',
    site: user.site?.name || 'N/A',
    roleId: user.roles?.id,
    siteId: user.site?.id || null,
    managerId: user.manager?.id || null,
    workShiftId: user.workShift?.id || user.workShiftId || null,
    staff_location: user.staff_location || null,
    coordinates: user.staff_location ? {
      latitude: parseFloat(user.staff_location.latitude),
      longitude: parseFloat(user.staff_location.longitude),
    } : undefined,
    personalInfo: {
      dateOfBirth: user.dateOfBirth || '',
      gender: (user.gender as any) || '',
      bloodGroup: (user.bloodGroup as any) || '',
      maritalStatus: (user.maritalStatus as any) || '',
      aadharNumber: user.aadharNumber || '',
      panNumber: user.panNumber || '',
      faceImage: null,
      address: user.address || {
        street: '',
        city: '',
        state: '',
        pincode: '',
      },
      emergencyContact: user.emergencyContact || {
        name: '',
        phone: '',
        relationship: '',
      },
    },
    healthInfo: {
      height: user.height || '',
      weight: user.weight || '',
      bloodPressure: user.bloodPressure || '',
      medicalConditions: user.medicalConditions || '',
      allergies: user.allergies || '',
      currentMedications: user.currentMedications || '',
      vaccinationStatus: user.vaccinationStatus || '',
      lastHealthCheckup: user.lastHealthCheckup || '',
      medicalCertificate: null,
    },
  };
};

// ASSIGN ROLE to user/staff
const assignRole = async (data: AssignRoleRequest): Promise<Staff> => {
  const response = await apiClient.patch<UserAPIResponse>('/api/user/role', { userId: data.userId, roleId: data.roleId });
  const user = response.data;

  return {
    id: user.id.toString(),
    name: user.name,
    userName: user.userName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    role: user.roles?.name || 'N/A',
    reportingTo: user.manager?.name || 'N/A',
    site: user.site?.name || 'N/A',
    roleId: user.roles?.id,
    siteId: user.siteId,
    managerId: user.managerId,
    workShiftId: user.workShift?.id || user.workShiftId || null,
    staff_location: user.staff_location || null,
    coordinates: user.staff_location ? {
      latitude: parseFloat(user.staff_location.latitude),
      longitude: parseFloat(user.staff_location.longitude),
    } : undefined,
  };
};

// DELETE user/staff
const deleteStaff = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/user/${id}`);
};

// ASSIGN SITE to staff
const assignSiteToStaff = async (data: UpdateSiteRequest): Promise<void> => {
  const payload: UpdateSiteRequest = {
    id: data.id,
    siteId: data.siteId,
    name: data.name,
    description: data.description,
    longitude: data.longitude,
    latitude: data.latitude,
    threshold_distance: data.threshold_distance,
    users: data.users,
  };

  const response = await apiClient.put('/api/site', payload);

  return response.data;
};

// GET user documents with SIGNED URLs
const fetchUserDocuments = async (userId: number): Promise<UserDocumentsResponse> => {
  const response = await apiClient.get<UserDocumentsResponse>(`/api/documents/user/${userId}`);
  return response.data;
};

// RESET PIN for user
const resetPin = async (userId: number, newPin: string): Promise<{ success: boolean; message: string; user: UserAPIResponse }> => {
  const response = await apiClient.patch('/api/user/reset-pin', {
    userId,
    newPin,
  });
  return response.data;
};

// ==================== QUERY HOOKS ====================

// Fetch all staffs
export const useStaffs = () => {
  return useQuery({
    queryKey: ['staffs'],
    queryFn: fetchStaffs,
    staleTime: 30000, // Cache for 30 seconds to avoid too many requests
  });
};

// Fetch single staff by ID
export const useStaff = (id: string) => {
  return useQuery({
    queryKey: ['staffs', id],
    queryFn: () => fetchStaffById(id),
    enabled: !!id,
  });
};

// ==================== MUTATION HOOKS ====================

// Create staff
export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] });
    },
  });
};

// Update staff
export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: updateStaff,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] });
      queryClient.invalidateQueries({ queryKey: ['staffs', data.id] });
    },
  });
};

// Assign role to staff
export const useAssignRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: assignRole,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] });
      queryClient.invalidateQueries({ queryKey: ['staffs', data.id] });
    },
  });
};

// Delete staff
export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] });
    },
  });
};

// Assign site to staff
export const useAssignSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignSiteToStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
};

// Register face for attendance
export const useRegisterFace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, faceImage }: { userId: number; faceImage: File }) =>
      registerFace(userId, faceImage),
    onSuccess: (_data, variables) => {
      // faceImageUrl is only returned by GET /api/user/:id, not the documents endpoint
      queryClient.invalidateQueries({ queryKey: ['staffs', variables.userId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['userDocuments', variables.userId] });
    },
  });
};

// Upload profile picture
export const useUploadProfilePicture = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, photo }: { userId: number; photo: File }) =>
      uploadProfilePicture(userId, photo),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['staffs', variables.userId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['userDocuments', variables.userId] });
    },
  });
};

// Upload medical certificate
export const useUploadMedicalCertificate = () => {
  return useMutation({
    mutationFn: ({ userId, certificate }: { userId: number; certificate: File }) =>
      uploadMedicalCertificate(userId, certificate),
  });
};

// Fetch user documents with signed URLs
export const useUserDocuments = (userId: number | null | undefined) => {
  return useQuery({
    queryKey: ['userDocuments', userId],
    queryFn: () => fetchUserDocuments(userId!),
    enabled: !!userId,
    staleTime: 3000000, // 50 minutes (signed URLs expire in 1 hour)
    gcTime: 3300000, // 55 minutes
  });
};

// Reset PIN for user
export const useResetPin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, newPin }: { userId: number; newPin: string }) =>
      resetPin(userId, newPin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] });
    },
  });
};